import { Match, Option } from 'effect';

import type { Page } from '@vs-shop/domain/pagination';

import type { ProductListing } from '../../capability/productListing/ProductListing';
import type { ProductListingError } from '../../capability/productListing/errors';
import type { FacetSet } from '../../capability/productListing/facetSet';
import type { ListingScope, ListingScopeKey } from '../../capability/productListing/listingScope';
import type { ListingChange, ListingState } from '../../capability/productListing/listingSelection';
import { ListingState as ListingStateSchema } from '../../capability/productListing/listingSelection';
import type { ProductSummary } from '../../capability/productSchema';
import { ListingBaseSchema, type ListingBase } from '../codec';
import type { ListingUrlSyntax } from '../url/ListingUrlSyntax';
import { type QueryPair } from '../url/listingUrl';
import { ListingVocabulary } from '../vocabulary/ListingVocabulary';
import type { ListingVocabulary as ListingVocabularyType } from '../vocabulary/ListingVocabulary';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';
import type * as Hydration from 'effect/unstable/reactivity/Hydration';

import type { ListingIntent } from './ListingPrefetch';
import { makeListingPrefetchAtoms } from './makeListingPrefetchAtoms';
export { ListingIntent } from './ListingPrefetch';

import { dehydrateValue } from './dehydrateValue';
import { HYDRATED_IDLE_TTL_MS } from './hydratedIdleTtl';
import { makeListingPageRun, type ListingResult } from './makeListingPageRun';
export type { ListingResult } from './makeListingPageRun';
export { ListingFacets, listingFacetSet } from './ListingFacets';
import type { ListingHistory } from './listingHistory';
import { resolveListingCacheOptions, type ListingCacheOptions } from './ListingCacheOptions';
import { makeListingQueries } from './makeListingQueries';
import { makeListingNavigation } from './makeListingNavigation';
import { makeListingFacetLayout } from './ListingFacetLayout';
export { listingCacheDefaults, type ListingCacheOptions } from './ListingCacheOptions';

export type ListingAtomsDeps<ER> = {
  readonly runtime: Atom.AtomRuntime<ProductListing, ER>;
  readonly syntax: ListingUrlSyntax;
  readonly history: ListingHistory;
  readonly itemsPerPage: number;
  readonly keyPrefix: string;
  readonly cache?: ListingCacheOptions;
  /** Enables page accumulation for a tenant with a load-more control. */
  readonly pageMode?: 'append' | 'replace';
  /** Keeps the first scoped facet layout while responses refresh counts. */
  readonly facetLayout?: 'fixed' | 'dynamic';
  /**
   * Selects the query pairs that are listing state on a scope.
   *
   * A browser pop decodes only these pairs. Every other pair stays out of the
   * listing base, so the next change does not write it.
   */
  readonly splitQuery: (
    scope: ListingScope,
    query: ReadonlyArray<QueryPair>
  ) => { readonly listing: ReadonlyArray<QueryPair> };
};

export type ListingBinding<ER> = {
  readonly current: Atom.Atom<AsyncResult.AsyncResult<ListingResult, ProductListingError | ER>>;
  readonly productCount: Atom.Atom<AsyncResult.AsyncResult<number, ProductListingError | ER>>;
  readonly state: Atom.Atom<ListingState>;
  readonly vocabulary: Atom.Atom<ListingVocabularyType>;
  readonly base: Atom.Atom<ListingBase>;
  readonly change: Atom.Writable<ListingState, ListingChange>;
  readonly loadNextPage?: Atom.Writable<ListingState, void>;
  readonly history: Atom.Atom<void>;
  readonly prefetch: Atom.Writable<void, ListingIntent>;
  readonly retry: Atom.Writable<void, void>;
};

export type ListingAtoms<ER = never> = {
  /** Binds all interactive listing state for one scope. */
  readonly listing: (scopeKey: ListingScopeKey) => ListingBinding<ER>;
  /** Binds the current listing result for one scope. */
  readonly total: (scopeKey: ListingScopeKey) => ListingBinding<ER>['current'];
  /** Encodes the server listing state for the shared browser registry. */
  readonly dehydrateListing: (input: {
    readonly scopeKey: ListingScopeKey;
    readonly base: ListingBase;
    readonly vocabulary: ListingVocabularyType;
    readonly state: ListingState;
    readonly products: Page<ProductSummary>;
    readonly facets: FacetSet | null;
  }) => ReadonlyArray<Hydration.DehydratedAtomValue>;
};

const unhydratedLeaf = <A>() =>
  Atom.writable<A, A>(
    () => {
      throw new Error('The listing atom needs hydration before its first read.');
    },
    (context, value) => context.setSelf(value)
  ).pipe(Atom.setIdleTTL(HYDRATED_IDLE_TTL_MS));

/** Creates the engine-owned atom cluster for one tenant binding. */
export const makeListingAtoms = <ER>(deps: ListingAtomsDeps<ER>): ListingAtoms<ER> => {
  const settings = resolveListingCacheOptions(deps.cache);
  const pageMode = Option.getOrElse(
    Option.fromUndefinedOr(deps.pageMode),
    (): 'replace' => 'replace'
  );
  const layoutMode = Option.getOrElse(
    Option.fromUndefinedOr(deps.facetLayout),
    (): 'dynamic' => 'dynamic'
  );
  const queries = makeListingQueries({
    runtime: deps.runtime,
    keyPrefix: deps.keyPrefix,
    settings,
  });
  const vocabularyAtom = Atom.family((scopeKey: ListingScopeKey) =>
    unhydratedLeaf<ListingVocabularyType>().pipe(
      Atom.serializable({
        key: `${deps.keyPrefix}/vocabulary/${scopeKey}`,
        schema: ListingVocabulary,
      })
    )
  );
  const baseAtom = Atom.family((scopeKey: ListingScopeKey) =>
    unhydratedLeaf<ListingBase>().pipe(
      Atom.serializable({ key: `${deps.keyPrefix}/base/${scopeKey}`, schema: ListingBaseSchema })
    )
  );
  const stateAtom = Atom.family((scopeKey: ListingScopeKey) =>
    unhydratedLeaf<ListingState>().pipe(
      Atom.serializable({ key: `${deps.keyPrefix}/state/${scopeKey}`, schema: ListingStateSchema })
    )
  );
  const productCountAtom = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.make((get) => {
      const state = get(stateAtom(scopeKey));
      const epoch = get(queries.epoch);
      const products = get(
        queries.products(
          {
            scope: state.scope,
            selection: state.selection,
            page: state.page,
            itemsPerPage: deps.itemsPerPage,
          },
          epoch
        ).current
      );
      return AsyncResult.map(products, (page) => page.total);
    }).pipe(Atom.setIdleTTL(HYDRATED_IDLE_TTL_MS))
  );

  const navigation = makeListingNavigation({
    state: stateAtom,
    base: baseAtom,
    vocabulary: vocabularyAtom,
    history: deps.history,
    syntax: deps.syntax,
    splitQuery: deps.splitQuery,
  });
  const pageRun = makeListingPageRun<ER>({
    state: navigation.state,
    epoch: queries.epoch,
    pageMode,
    itemsPerPage: deps.itemsPerPage,
    products: (state, page, epoch) =>
      queries.products({ ...state, page, itemsPerPage: deps.itemsPerPage }, epoch),
    facets: (state, epoch) => queries.facets(state, epoch),
    retain: queries.retain,
    firstPage: navigation.firstPage,
    append: navigation.append,
  });

  const fixedFacetLayout = makeListingFacetLayout({
    keyPrefix: deps.keyPrefix,
    epoch: queries.epoch,
    pageRunCurrent: pageRun.current,
  });
  const current = Atom.family((scopeKey: ListingScopeKey) =>
    Match.value(layoutMode).pipe(
      Match.when('fixed', () => fixedFacetLayout.current(scopeKey)),
      Match.when('dynamic', () => pageRun.current(scopeKey)),
      Match.exhaustive
    )
  );

  const prefetch = makeListingPrefetchAtoms({
    state: stateAtom,
    queries,
    itemsPerPage: deps.itemsPerPage,
    settings,
  });

  const dehydrateListing = (input: {
    readonly scopeKey: ListingScopeKey;
    readonly base: ListingBase;
    readonly vocabulary: ListingVocabularyType;
    readonly state: ListingState;
    readonly products: Page<ProductSummary>;
    readonly facets: FacetSet | null;
  }): ReadonlyArray<Hydration.DehydratedAtomValue> => {
    const request = {
      scope: input.state.scope,
      selection: input.state.selection,
      page: input.state.page,
      itemsPerPage: deps.itemsPerPage,
    };
    const values = [
      dehydrateValue(vocabularyAtom(input.scopeKey), input.vocabulary),
      dehydrateValue(baseAtom(input.scopeKey), input.base),
      dehydrateValue(stateAtom(input.scopeKey), input.state),
      ...Match.value(layoutMode).pipe(
        Match.when('fixed', () => [fixedFacetLayout.dehydrate(input.scopeKey, input.facets)]),
        Match.when('dynamic', () => []),
        Match.exhaustive
      ),
      queries.dehydrateProducts(request, input.products),
    ];
    return Option.match(Option.fromNullishOr(input.facets), {
      onNone: () => values,
      onSome: (facets) => [...values, queries.dehydrateFacets(input.state, facets)],
    });
  };

  const listing = Atom.family((scopeKey: ListingScopeKey): ListingBinding<ER> => ({
    current: current(scopeKey),
    productCount: productCountAtom(scopeKey),
    state: navigation.state(scopeKey),
    vocabulary: vocabularyAtom(scopeKey),
    base: baseAtom(scopeKey),
    change: navigation.change(scopeKey),
    ...Match.value(pageMode).pipe(
      Match.when('append', () => ({ loadNextPage: pageRun.loadNextPage(scopeKey) })),
      Match.when('replace', () => ({})),
      Match.exhaustive
    ),
    history: navigation.history(scopeKey),
    prefetch: prefetch(scopeKey),
    retry: pageRun.retry(scopeKey),
  }));

  return {
    listing,
    total: (scopeKey) => current(scopeKey),
    dehydrateListing,
  };
};
